import clsx from 'clsx';
import { KeenIcon } from '@/components/keenicons';
import {
  Menu,
  MenuArrow,
  MenuBadge,
  MenuBullet,
  MenuHeading,
  MenuIcon,
  MenuItem,
  MenuLabel,
  MenuLink,
  MenuSub,
  MenuTitle
} from '@/components/menu';
import { useMenus } from '@/providers';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faCreditCard,
  faWallet,
  faFileLines,
  faMobile,
  faHeadset,
  faClockRotateLeft,
  faChartColumn
  // ... thêm các icon bạn cần
} from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

const iconMap = {
  faUser,
  faCreditCard,
  faWallet,
  faFileLines,
  faMobile,
  faHeadset,
  faClockRotateLeft,
  faChartColumn
  // ... map thêm icon nếu cần
};

const SidebarMenu = ({ isCollapsed }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (item) => item.path && currentPath === item.path || currentPath.startsWith(item.path + '/');

  const textColor = 'text-white group-hover:text-gray-900';
  const iconColor = 'text-white group-hover:text-gray-700 group-hover:font-normal';

  const linkPl = 'ps-[10px]';
  const linkPr = 'pe-[10px]';
  const linkPy = 'py-[10px]';
  const itemsGap = 'gap-0.5';
  const subLinkPy = 'py-[8px]';
  const rightOffset = 'me-[-10px]';
  const iconWidth = 'w-[20px]';
  const iconSize = 'text-lg';
  const accordionLinkGap = ['gap-[10px]'];
  const accordionPl = ['ps-[10px]'];
  const accordionBorderLeft = ['before:start-[20px]'];

  const buildMenu = items =>
    items.map((item, index) => {
      if (item.separator) {
        return <div key={`sep-${index}`} className="mt-8 mb-2 border-t border-gray-200 dark:border-gray-700" />;
      }
      if (item.heading) {
        return (
          <MenuItem key={index} className="pt-2.25 pb-px">
            <MenuHeading className={clsx('uppercase text-2sm font-medium', textColor, linkPl, linkPr)}>
              {item.heading}
            </MenuHeading>
          </MenuItem>
        );
      }
      if (item.disabled) return buildMenuItemDisabled(item, index);
      return buildMenuItem(item, index);
    });

  const buildMenuItem = (item, index) => {
    const isItemActive = isActive(item);

    if (item.children) {
      return (
        <MenuItem
          key={index}
          className={clsx(
            'mb-4 last:mb-0 group',
            isCollapsed
              ? (isItemActive ? 'bg-[#7EC587] rounded-sm' : '')
              : (isItemActive ? 'bg-[#50B553] rounded-sm' : 'bg-[#F8F8F8] rounded-sm')
          )}
        >
          <MenuLink
            className={clsx(
              'flex items-center grow cursor-pointer border border-transparent',
              accordionLinkGap[0],
              linkPl,
              linkPr,
              linkPy
            )}
          >
            <MenuIcon className={clsx(iconWidth, iconColor)}>{renderIcon(item)}</MenuIcon>
            <MenuTitle className={clsx('text-base font-medium', textColor)}>{item.title}</MenuTitle>
            <MenuArrow className={clsx('text-gray-400 w-[20px] shrink-0 justify-end ms-1', rightOffset)}>
              <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
              <KeenIcon icon="minus" className="text-2xs hidden menu-item-show:inline-flex" />
            </MenuArrow>
          </MenuLink>
          <MenuSub
            className={clsx(
              'relative before:absolute before:top-0 before:bottom-0 before:border-s before:border-gray-200',
              itemsGap,
              accordionBorderLeft[0],
              accordionPl[0]
            )}
          >
            {buildMenuItemChildren(item.children, index)}
          </MenuSub>
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={index}
        className={clsx(
          'mb-4 last:mb-0 group hover:bg-[#50B553] hover:rounded-lg',
          isCollapsed
            ? (isItemActive ? 'bg-[#7EC587] border-0 rounded-lg' : '')
            : (isItemActive ? 'bg-[#50B553]' : 'bg-[#884040]')
        )}
      >
        <MenuLink
          path={item.path}
          className={clsx(
            'border border-transparent flex items-center',
            accordionLinkGap[0],
            linkPl,
            linkPr,
            linkPy
          )}
        >
          <MenuIcon className={clsx(iconWidth, iconColor)}>{renderIcon(item)}</MenuIcon>
          <MenuTitle className={clsx('text-sm font-normal', textColor)}><FormattedMessage id={item.title} /></MenuTitle>
        </MenuLink>
      </MenuItem>
    );
  };

  const buildMenuItemChildren = (items, parentIndex, level = 0) =>
    items.map((item, index) => {
      const isItemActive = isActive(item);
      if (item.disabled) return buildMenuItemDisabled(item, `${parentIndex}-${index}`);

      return (
        <MenuItem
          key={index}
          className={clsx(
            'group',
            isCollapsed
              ? (isItemActive ? 'bg-[#7EC587]' : '')
              : (isItemActive ? 'bg-[#50B553]' : 'bg-[#F8F8F8]')
          )}
        >
          <MenuLink
            path={item.path}
            className={clsx(
              'border border-transparent flex items-center',
              accordionLinkGap[level],
              accordionPl[level],
              subLinkPy
            )}
          >
            <MenuBullet className="w-[6px] relative before:absolute before:top-0 before:size-[6px] before:rounded-full menu-item-active:before:bg-primary menu-item-hover:before:bg-primary" />
            <MenuIcon className={clsx(iconWidth, iconColor)}>{renderIcon(item)}</MenuIcon>
            <MenuTitle className={clsx('text-sm font-normal', textColor)}>{item.title}</MenuTitle>
          </MenuLink>
        </MenuItem>
      );
    });

  const buildMenuItemDisabled = (item, index) => (
    <MenuItem key={index} className="mb-4 last:mb-0">
      <MenuLabel className={clsx('border border-transparent', accordionLinkGap[0], linkPy, linkPl, linkPr)}>
        <MenuIcon className={clsx(iconWidth, iconColor)}>{renderIcon(item)}</MenuIcon>
        <MenuTitle className={clsx('text-sm font-normal', textColor)}>{item.title}</MenuTitle>
        <MenuBadge className={rightOffset}><span className="badge badge-xs">Soon</span></MenuBadge>
      </MenuLabel>
    </MenuItem>
  );

  const renderIcon = (item) => {
    // Nếu icon là FontAwesome
    if (item.icon && iconMap[item.icon]) {
      return <FontAwesomeIcon icon={iconMap[item.icon]} className={clsx(iconSize, iconColor)} />;
    }
    // Nếu là ảnh
    if (item.img) {
      return <img src={item.img} alt={item.title} className="object-contain" style={{ width: item.imgWidth ?? 20, height: item.imgHeight ?? 20 }} />;
    }
    // Nếu là KeenIcon hoặc icon khác
    if (item.icon) {
      return <KeenIcon icon={item.icon} className={clsx(iconSize, iconColor)} />;
    }
    return null;
  };

  const { getMenuConfig } = useMenus();
  const menuConfig = getMenuConfig('primary');

  return (
    <Menu highlight multipleExpand={false} className={clsx('flex flex-col grow', itemsGap)}>
      {menuConfig && buildMenu(menuConfig)}
    </Menu>
    
  );
};

export { SidebarMenu };
